// src/pages/business/LocationChangePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { getBusinessSettings } from "@/lib/api";


const WD_LABELS = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];

function Section({ title, right, children }) {
  return (
    <div className="rounded-2xl border p-4 mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-medium">{title}</h2>
        {right || null}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center py-2">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}

// ——— helpers ———

// ia un „hint" de business din payload, indiferent de schemă
function pickBusinessHint(loc) {
  if (!loc) return null;

  // 1) obiect business {id, name}
  if (loc.business && (loc.business.id != null || loc.business.name)) {
    return loc.business;
  }

  // 2) business ca ID (number/string)
  if (typeof loc.business === "number" || (typeof loc.business === "string" && loc.business !== "")) {
    return { id: Number(loc.business), name: "" };
  }

  // 3) chei alternative pentru id / name
  const idLike =
    loc.business_id ??
    loc.businessId ??
    loc.company_id ??
    loc.companyId ??
    loc.firm_id ??
    loc.firmId ??
    null;

  const nameLike =
    loc.business_name ??
    loc.businessName ??
    loc.company_name ??
    loc.companyName ??
    loc.firm_name ??
    loc.firmName ??
    "";

  if (idLike != null || nameLike) {
    return { id: idLike ?? null, name: nameLike || "" };
  }

  // 4) „company" ca obiect
  if (loc.company && (loc.company.id != null || loc.company.name)) {
    return loc.company;
  }

  // nimic util
  return null;
}

function pickBusiness(loc) {
  // din LocationSerializer: business poate fi obiect sau ai business_name separat
  const id =
    loc?.business?.id ??
    loc?.business_id ??
    (typeof loc?.business === "number" ? loc.business : null);
  const name =
    loc?.business?.name ??
    loc?.business_name ??
    "";
  return { id: id ?? null, name: name || "" };
}

// normalizează numele sucursalei indiferent de schemă
function pickLocationName(loc) {
  return loc?.name ?? loc?.display_name ?? loc?.title ?? loc?.location_name ?? "";
}

// încearcă să rezolve {id,name} pentru business
async function resolveBusinessName(hint) {
  // 1) dacă avem deja numele
  if (hint && hint.name) {
    return { id: hint.id ?? null, name: hint.name };
  }

  // 2) avem doar id -> încercăm pe rute posibile
  if (hint && hint.id != null) {
    // a) detaliu (dacă există)
    try {
      const { data } = await api.get(`/b/businesses/${hint.id}`);
      if (data?.name) return { id: data.id ?? hint.id, name: data.name };
    } catch { }

    try {
      const { data } = await api.get(`/b/business/${hint.id}`);
      if (data?.name) return { id: data.id ?? hint.id, name: data.name };
    } catch { }

    // b) listă de business-uri (folosită de BusinessSelector)
    try {
      const { data } = await api.get(`/b/businesses`);
      const item = (Array.isArray(data) ? data : []).find(b => Number(b.id) === Number(hint.id));
      if (item?.name) return { id: item.id, name: item.name };
    } catch { }

    // c) setări de business (fallback folosit pe BusinessSettings)
    try {
      const { data } = await api.get(`/b/settings`, { params: { business_id: hint.id } });
      // ajustează cheia dacă la tine e alta (ex. data.business.name)
      const name = data?.name ?? data?.business?.name;
      if (name) return { id: hint.id, name };
    } catch { }

    // ultimul fallback: avem doar id
    return { id: hint.id, name: "" };
  }

  // 3) nu avem nici hint valid
  return { id: null, name: "" };
}


export default function LocationChangePage() {
  const { id } = useParams();
  const locId = useMemo(() => Number(id), [id]);

  const [loading, setLoading] = useState(true);
  // const [savingLocation, setSavingLocation] = useState(false);
  const [msg, setMsg] = useState("");

  // Location core
  const [business, setBusiness] = useState({ id: null, name: "" });
  const [name, setName] = useState("");
  const [status, setStatus] = useState("active");

  // Working hours
  const [whLoading, setWhLoading] = useState(true);
  const [whSaving, setWhSaving] = useState(false);
  const [wh, setWh] = useState({ "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] });

  // Roles
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [roles, setRoles] = useState([]);

  // --- load location + business name (cu fallback-uri) ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // încearcă /b/locations/:id, apoi /b/location/:id, apoi lista
        let loc;
        try {
          const { data } = await api.get(`/b/locations/${locId}`);
          loc = data;
        } catch {
          try {
            const { data } = await api.get(`/b/location/${locId}`);
            loc = data;
          } catch {
            const { data: list } = await api.get(`/b/locations`);
            loc = (Array.isArray(list) ? list : []).find(
              (x) => Number(x.id) === Number(locId)
            );
            if (!loc) throw new Error("Location not found");
          }
        }

        const biz = await resolveBusinessName(pickBusinessHint(loc));

        if (!mounted) return;
        setBusiness(biz);
        setName(pickLocationName(loc));
        // setStatus(loc?.status || "active");
        
      } catch {
        if (mounted) setMsg("Nu am putut încărca informațiile sucursalei.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [locId]);

  // --- load working hours ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setWhLoading(true);
        const { data } = await api.get("/b/working-hours", { params: { location_id: String(locId) } });
        if (!mounted) return;
        const normalized = { "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] };
        for (let i = 0; i < 7; i++) {
          const k = String(i);
          const lst = Array.isArray(data?.recurring?.[k]) ? data.recurring[k] : [];
          normalized[k] = lst.length ? [{ start: lst[0].start, end: lst[0].end }] : [];
        }
        setWh(normalized);
      } catch {
        // lăsăm gol
      } finally {
        setWhLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [locId]);

  // --- load roles ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setRolesLoading(true);
        const { data } = await api.get("/b/location-roles", {
          params: { location_id: String(locId) },
        });
        if (!mounted) return;
        const rows = Array.isArray(data) ? data : [];
        setRoles(rows.map(r => ({
          id: r.id,
          user_id: r.user?.id,
          user_name: r.user?.name || r.user?.username || r.user?.email || "",
          role: r.role,
        })));
      } catch {
        // ignore
      } finally {
        setRolesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [locId]);

  // const saveLocation = async () => {
  //   setSavingLocation(true);
  //   setMsg("");
  //   try {
  //     try {
  //       await api.patch(`/b/locations/${locId}`, { status });
  //     } catch {
  //       await api.patch(`/b/location/${locId}`, { status });
  //     }
  //     setMsg("Location salvat.");
  //   } catch (e) {
  //     setMsg("Eroare la salvarea sucursalei.");
  //   } finally {
  //     setSavingLocation(false);
  //     setTimeout(() => setMsg(""), 4000);
  //   }
  // };

  const setClosed = (i, closed) => {
    const k = String(i);
    setWh(prev => ({
      ...prev,
      [k]: closed ? [] : [{ start: "09:00", end: "17:00" }],
    }));
  };
  const setTime = (i, field, val) => {
    const k = String(i);
    setWh(prev => {
      const cur = Array.isArray(prev[k]) ? prev[k] : [];
      const item = cur[0] || { start: "09:00", end: "17:00" };
      return { ...prev, [k]: [{ ...item, [field]: val }] };
    });
  };
  const saveWH = async () => {
    setWhSaving(true);
    try {
      await api.put("/b/working-hours", { location_id: locId, recurring: wh });
      setMsg("Program de lucru salvat.");
    } catch {
      setMsg("Eroare la salvarea programului.");
    } finally {
      setWhSaving(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  const addRoleRow = () => {
    setRoles(prev => [...prev, { id: `new-${Date.now()}`, user_id: "", user_name: "", role: "" }]);
  };
  const updateRoleRow = (idx, key, val) => {
    setRoles(prev => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };
  const removeRoleRow = (idx) => {
    setRoles(prev => prev.filter((_, i) => i !== idx));
  };
  const saveRoles = async () => {
    setRolesSaving(true);
    try {
      const payload = roles.map(r => ({
        id: typeof r.id === "number" ? r.id : undefined,
        location_id: locId,
        user_id: r.user_id,
        role: r.role,
      }));
      await api.put("/b/location-roles", payload);
      setMsg("Rolurile au fost salvate.");
    } catch {
      setMsg("Eroare la salvarea rolurilor.");
    } finally {
      setRolesSaving(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Se încarcă…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header + breadcrumb */}
      <div className="mb-4">
        <div className="text-xl font-semibold">
          {name || "—"}
        </div>
        <div className="mt-2">
          <a
            className="text-sm underline"
            href={`/admin/core/location/${locId}/history/`}
            target="_blank"
            rel="noreferrer"
          >
            History
          </a>
        </div>
      </div>

      {msg ? (
        <div className="mb-4 text-sm px-3 py-2 rounded bg-gray-50 border border-gray-200">
          {msg}
        </div>
      ) : null}

      {/* Business / Name / Status */}
      {/* <Section title="Business">
        <Row label="Sediu:">
          <div className="text-sm">{name || "—"}</div>
        </Row>

        <Row label="Status:">
          <select
            className="w-full border rounded px-3 py-2"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="active">active</option>
            <option value="closed">closed</option>
          </select>
        </Row>

        <div className="pt-2">
          <button
            onClick={saveLocation}
            disabled={savingLocation}
            className="px-4 py-2 rounded border"
          >
            {savingLocation ? "Se salvează…" : "Salvează"}
          </button>
        </div>
      </Section> */}

      {/* Working hours */}
      <Section title="Working hours">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-2 border">Weekday</th>
                <th className="text-left p-2 border">Is closed</th>
                <th className="text-left p-2 border">Open time</th>
                <th className="text-left p-2 border">Close time</th>
              </tr>
            </thead>
            <tbody>
              {whLoading ? (
                <tr>
                  <td className="p-2 border" colSpan={4}>Se încarcă…</td>
                </tr>
              ) : (
                Array.from({ length: 7 }).map((_, i) => {
                  const k = String(i);
                  const closed = !(wh[k]?.length);
                  const start = wh[k]?.[0]?.start || "09:00";
                  const end = wh[k]?.[0]?.end || "17:00";
                  return (
                    <tr key={i}>
                      <td className="p-2 border">{WD_LABELS[i]}</td>
                      <td className="p-2 border">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={closed}
                            onChange={(e) => setClosed(i, e.target.checked)}
                          />
                          Închis
                        </label>
                      </td>
                      <td className="p-2 border">
                        <input
                          type="time"
                          disabled={closed}
                          className="w-full border rounded px-2 py-1"
                          value={start}
                          onChange={(e) => setTime(i, "start", e.target.value)}
                        />
                      </td>
                      <td className="p-2 border">
                        <input
                          type="time"
                          disabled={closed}
                          className="w-full border rounded px-2 py-1"
                          value={end}
                          onChange={(e) => setTime(i, "end", e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="pt-2">
          <button
            onClick={saveWH}
            disabled={whSaving || whLoading}
            className="px-4 py-2 rounded border"
          >
            {whSaving ? "Se salvează…" : "Salvează"}
          </button>
        </div>
      </Section>

      {/* Location roles */}
      <Section title="Location roles">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-2 border">User</th>
                <th className="text-left p-2 border">Role</th>
                <th className="text-left p-2 border">Delete?</th>
              </tr>
            </thead>
            <tbody>
              {rolesLoading ? (
                <tr>
                  <td className="p-2 border" colSpan={3}>Se încarcă…</td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td className="p-2 border text-gray-600" colSpan={3}>—</td>
                </tr>
              ) : (
                roles.map((r, idx) => (
                  <tr key={r.id ?? idx}>
                    <td className="p-2 border">
                      <input
                        className="w-full border rounded px-2 py-1"
                        placeholder="user id"
                        value={r.user_id ?? ""}
                        onChange={(e) => updateRoleRow(idx, "user_id", e.target.value)}
                      />
                      <div className="text-xs text-gray-500 mt-1">{r.user_name}</div>
                    </td>
                    <td className="p-2 border">
                      <select
                        className="w-full border rounded px-2 py-1"
                        value={r.role ?? ""}
                        onChange={(e) => updateRoleRow(idx, "role", e.target.value)}
                      >
                        <option value=""></option>
                        <option value="director">director</option>
                        <option value="reception">reception</option>
                        <option value="employee">employee</option>
                      </select>
                    </td>
                    <td className="p-2 border align-top">
                      <button className="underline" onClick={() => removeRoleRow(idx)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <button className="px-3 py-2 rounded border" onClick={addRoleRow}>
            Add another Location role
          </button>
          <button
            className="px-3 py-2 rounded border"
            onClick={saveRoles}
            disabled={rolesSaving || rolesLoading}
          >
            {rolesSaving ? "Se salvează…" : "Salvează"}
          </button>
        </div>
      </Section>
    </div>
  );
}